// src/core/fixture-db.js — capa de datos del Fixture del Mundial.
//
// FASE 4 · Bloque "Fixture conectado" (21-may-2026). Lectura directa de
// Supabase de las tres tablas del Mundial: `partidos` (104), `equipos` (48)
// y `posiciones_grupo`. Las tres tienen RLS con SELECT público (`using=true`),
// así que el cliente anon/autenticado las lee sin restricción.
//
// Funciones puras de lectura — no tocan el DOM, no tocan localStorage. Las
// pantallas (src/modules/fixture.js, grupo.js) consumen estas funciones.
//
// Caché en memoria con TTL de 60s: el fixture cambia raramente (solo cuando
// FIFA actualiza resultados), no tiene sentido pegarle a Supabase en cada
// render. F1.4 (23-may) lo reemplaza con Supabase Realtime.
//
// CERO hardcodeo de cantidades: los grupos del Mundial, la cantidad de
// equipos y de partidos salen siempre de lo que devuelve la base. Si la base
// devuelve 12 grupos se pintan 12; si mañana son 11, se pintan 11.

import { supabase } from '../supabase.js'
import { mismoDiaART } from '../utils/fecha-art.js'

const TTL_MS = 60_000
const cache = new Map() // key → { at: epoch_ms, data }

const cached = async (key, loader) => {
  const hit = cache.get(key)
  if (hit && Date.now() - hit.at < TTL_MS) return hit.data
  const data = await loader()
  cache.set(key, { at: Date.now(), data })
  return data
}

// Invalida la caché — útil para un "refrescar" manual desde la UI.
export const clearFixtureCache = () => cache.clear()

// ─── Loaders crudos (un fetch por tabla, cacheado) ─────────────────────────

const loadEquipos = async () => {
  const { data, error } = await supabase
    .from('equipos')
    .select('equipo_id, external_id, nombre, codigo, grupo_mundial, bandera_url')
    .order('grupo_mundial', { ascending: true })
    .order('nombre', { ascending: true })
  if (error) throw new Error('[fixture-db] equipos: ' + error.message)
  return data || []
}

const loadPartidos = async () => {
  const { data, error } = await supabase
    .from('partidos')
    .select('*')
    .order('match_number', { ascending: true })
  if (error) throw new Error('[fixture-db] partidos: ' + error.message)
  return data || []
}

const loadPosiciones = async () => {
  const { data, error } = await supabase
    .from('posiciones_grupo')
    .select('*')
    .order('grupo_mundial', { ascending: true })
    .order('posicion', { ascending: true })
  if (error) throw new Error('[fixture-db] posiciones_grupo: ' + error.message)
  return data || []
}

// ─── API pública ───────────────────────────────────────────────────────────

// Todos los equipos del Mundial.
export const fetchEquipos = () => cached('equipos', loadEquipos)

// Partidos, opcionalmente filtrados. `filtros` admite: fase, grupo (= grupo
// del Mundial A-L), status, fecha (Date o string — compara solo el día).
export const fetchPartidos = async (filtros = {}) => {
  const todos = await cached('partidos', loadPartidos)
  const { fase = null, grupo = null, status = null, fecha = null } = filtros
  return todos.filter((p) =>
    (fase === null || p.fase === fase) &&
    (grupo === null || p.grupo_mundial === grupo) &&
    (status === null || p.status === status) &&
    (fecha === null || mismoDia(p.fecha_hora, fecha))
  )
}

// Grupos del Mundial agrupados dinámicamente desde `equipos`. Devuelve
// [{ grupo: 'A', equipos: [...] }, ...] — la cantidad sale de la base.
export const fetchGruposMundial = async () => {
  const equipos = await fetchEquipos()
  const porGrupo = new Map()
  for (const e of equipos) {
    if (e.grupo_mundial == null) continue
    if (!porGrupo.has(e.grupo_mundial)) porGrupo.set(e.grupo_mundial, [])
    porGrupo.get(e.grupo_mundial).push(e)
  }
  return [...porGrupo.entries()]
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([grupo, equiposDelGrupo]) => ({ grupo, equipos: equiposDelGrupo }))
}

// Tabla de posiciones de un grupo (o todas si no se pasa argumento).
// Hoy `posiciones_grupo` está vacía (el torneo no empezó) — devuelve [].
export const fetchPosicionesGrupo = async (grupoMundial = null) => {
  const todas = await cached('posiciones', loadPosiciones)
  return grupoMundial == null
    ? todas
    : todas.filter((p) => p.grupo_mundial === grupoMundial)
}

// Partidos de fase eliminatoria (todo lo que no es fase de grupos), ordenados
// por match_number. Incluye viene_local_de / viene_visitante_de tal cual
// vienen de la base (hoy NULL — los cruces todavía no están definidos).
export const fetchBracketEliminatoria = async () => {
  const todos = await cached('partidos', loadPartidos)
  return todos.filter((p) => p.fase !== 'grupo')
}

// ─── Helpers internos ──────────────────────────────────────────────────────

// DT-BL: comparación de día calendario en hora Argentina (no en TZ del
// navegador). Antes usaba getFullYear/Month/Date sobre `new Date(ts)`, que
// dependía de la TZ del runtime; con mismoDiaART el día se mide siempre en
// America/Argentina/Buenos_Aires.
const mismoDia = (ts, fecha) => mismoDiaART(ts, fecha)
